import React, { useState, useEffect, useRef } from "react";
import { Button } from "antd-mobile";
import { isMobile } from "@/utils/is";
import DownloadMask from "@/app/(routeLayout)/products/(components)/DownloadMask";
import { appendUrlParams } from "@/utils/url";
import { ROUTE_MAP } from "@/shared";
import type { TProductInfo } from "@/typings";
import { TrackingVisitSource, TrackingZoneName } from "@/shared";
import { useTrack } from "@/hooks/useTrack";
import { TrackingPoints } from "../../points";
import { SystemQRcodeOutline } from "antd-mobile-icons";
import { jumpProductToApp } from "@/utils/openApp";
import { getAppDeepLinkUrlHashId } from "@/utils/url";
import ExclusiveEarningMask from "@/components/ExclusiveEarningMask";
import { useProductStore } from "@/store/product";
import BottomBarSkeleton from "./skeleton";
import "./index.scss";

const classPrefix = "yfm-product-detail-bottom-bar";

interface BottomBarProps {
  productInfo: TProductInfo | null;
}

const BottomBar: React.FC<BottomBarProps> = ({ productInfo }) => {
  const { tracking } = useTrack();
  const [loading, setLoading] = useState(false);
  const [showMask, setShowMask] = useState(false);
  const showMaskTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [showExclusiveEarningMask, setShowExclusiveEarningMask] =
    useState(false);
  const [mounted, setMounted] = useState(false);

  const setCurrentHashId = useProductStore((state) => state.setCurrentHashId);

  useEffect(() => {
    setMounted(true);
  }, []);

  const closeMask = () => {
    setShowMask(false);
    if (isMobile()) {
      appendUrlParams(ROUTE_MAP.product.list);
    }
  };

  const handleDownload = () => {
    setLoading(true);
    const hashId = productInfo?.hash_id || "";
    if (hashId) {
      setCurrentHashId(hashId);
    }
    const appParams = {
      visit_source: TrackingVisitSource.ProductDetails,
      zone_name: TrackingZoneName.ProductDetails,
      api_params: "",
    };
    // 1. 打开 YooFinds 不带参数  2. 打开应用商店携带 deepLink
    jumpProductToApp(hashId, appParams, getAppDeepLinkUrlHashId(hashId));
    setTimeout(() => {
      setLoading(false);
    }, 3000);
  };

  const handleFreeSample = () => {
    tracking({
      ...TrackingPoints.FreeSample,
      product_id: productInfo?.product_id,
    });
    if (isMobile()) {
      handleDownload();
    } else {
      setShowExclusiveEarningMask(true);
    }
  };

  const handleShowcase = () => {
    tracking({
      ...TrackingPoints.AddToShowcase,
      product_id: productInfo?.product_id,
    });
    if (isMobile()) {
      if (productInfo?.campaign_short_url) {
        window.location.href = productInfo?.campaign_short_url;
      }
      showMaskTimerRef.current = setTimeout(() => {
        setShowMask(true);
      }, 2000);
    } else {
      setShowMask(true);
    }
  };

  useEffect(() => {
    if (showMask || showExclusiveEarningMask) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [showMask, showExclusiveEarningMask]);

  useEffect(() => {
    if (productInfo && !isMobile()) {
      setShowMask(true);
    }
  }, [productInfo]);

  useEffect(() => {
    return () => {
      if (showMaskTimerRef.current) {
        clearTimeout(showMaskTimerRef.current);
        showMaskTimerRef.current = null;
      }
    };
  }, []);

  if (!productInfo) return <BottomBarSkeleton />;

  return (
    <div className={`${classPrefix}__wrapper`}>
      <div className={`${classPrefix} yfm-app-page-width-fix`}>
        <div className={`${classPrefix}__add`} onClick={handleShowcase}>
          Add To Showcase
        </div>
        <Button
          className={`${classPrefix}__btn`}
          onClick={handleFreeSample}
          loading={loading}
        >
          Request Free Sample
        </Button>
      </div>
      {showMask && (
        <DownloadMask
          pageId={14}
          productId={productInfo?.product_id || ""}
          cover={productInfo?.cover || ""}
          ttLink={productInfo?.campaign_short_url || ""}
          onClose={closeMask}
          onDownload={handleDownload}
        />
      )}
      {showExclusiveEarningMask && (
        <ExclusiveEarningMask
          onClose={() => setShowExclusiveEarningMask(false)}
        />
      )}
      {mounted && !isMobile() && (
        <div className={`${classPrefix}__qr-code`} onClick={handleShowcase}>
          <SystemQRcodeOutline
            className={`${classPrefix}__qr-code-icon`}
            color="#25f4ee"
          />
        </div>
      )}
    </div>
  );
};

export default BottomBar;
